import React, { useState } from 'react'
import SoundOutDrill from './SoundOutDrill'
import styles from './SyllableTile.module.scss'
import { decomposeWord, alignWord, soundOf, partLabel, pronunciationLine, lower } from '../util/turkish'
import { TUR_WORDS } from '../data/drills/turWords'
import voices from '../speech/voices'

// "Sound it out" for Turkish. Syllables are drawn as tiles of letters; the
// vowel tile is as wide as the vowel is long — stretched for a stressed or
// long vowel (ğ, â, loanwords), squeezed for an unstressed ı i u ü — and the
// stressed syllable carries the ˈ mark. The sweep spends its time the same way.

const shuffle = (a) => { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j], b[i]] } return b }

const buildItems = (lengths, custom, count) => {
  const own = String(custom || '').split(/[,;\n]+/).map(s => s.trim()).filter(Boolean)
    .map(s => { const [k, e] = s.split('='); return { k: k.trim(), e: (e || '').trim(), estimated: true } })
    .filter(w => /[a-zçğıöşüâîû]/i.test(w.k))
  const pool = shuffle((lengths || []).flatMap(n => (TUR_WORDS[n] || []).map(w => ({ k: w.tr, e: w.en, s: w.s, l: w.l }))))
  return [...own, ...pool].slice(0, Math.max(count || 10, own.length))
}

// One syllable as a row of letter tiles.
function SyllableTile({ syl, states, size, onPart }) {
  const scale = size / 128
  return (
    <div className={`${styles.tile} ${syl.stressed ? styles.stressed : ''}`} style={{ fontSize: `${2.1 * scale}rem` }}>
      {syl.stressed ? <span className={styles.stressMark} title="stressed syllable — a little louder and longer">ˈ</span> : null}
      {syl.parts.map((p, pi) => (
        <span key={pi}
          className={`${styles.letter} ${styles['r_' + p.role]} ${styles[states[pi] || 'idle']} ${p.silent ? styles.silent : ''} ${p.long ? styles.long : ''} ${p.short ? styles.short : ''}`}
          style={p.role === 'N' ? { minWidth: `${1.05 * (p.factor || 1)}em` } : undefined}
          onClick={(e) => { e.stopPropagation(); onPart(pi) }}
          title={p.silent ? 'silent — lengthens the vowel before' : undefined}>
          {p.text}{p.long ? <span className={styles.lengthMark}>ː</span> : null}
        </span>
      ))}
    </div>
  )
}

// How a syllable's time is SHARED between its letters: consonants are quick;
// the vowel takes the weight, stretched or squeezed by the same factor as its
// tile. The unit is a first guess (~150 ms per syllable at rate 1); the drill
// scales the whole sweep to the measured length of the word.
const timing = (syl, rate) => {
  const unit = 50 / rate
  return syl.parts.map(p => unit * (p.role === 'N' ? 1.6 * (p.factor || 1) : p.silent ? 0.3 : p.role === 'O' ? 0.7 : 0.8))
}

const plugin = {
  title: 'Sound it out · Turkish',
  langCode: 'tur',
  voiceURI: voices['tur'] || voices['eng'],
  cps: 11,
  decompose: (item) => decomposeWord(item.k, { stress: item.s, long: item.l }),
  align: (item, transcript) => alignWord(item.k, transcript, { stress: item.s, long: item.l }),
  soundOf: (p, syl) => soundOf(p, syl),
  partLabel: (p, syl) => (p.role === 'N' ? partLabel(p) : `${partLabel(p)} — heard inside "${syl.text}"`),
  partText: (p) => p.text,
  norm: (s) => lower(s), // Turkish-aware lowercase: İ → i, I → ı
  syllableLabel: (syl) => `${syl.text}${syl.stressed ? ' · stressed' : ''}${syl.long ? ' · long vowel' : syl.short ? ' · short vowel' : ''}`,
  Block: SyllableTile,
  timing,
  pronunciation: (syls) => pronunciationLine(syls),
  legend: (syls, item) => `ˈ stressed syllable${item.estimated ? ' (estimated — last syllable, or before -yor)' : ''} · ː long vowel · ğ is silent and lengthens the vowel before it · a narrow vowel is a short one`,
  note: 'Tap a vowel to hear it alone, a consonant to hear its syllable. Stress in Turkish is gentle — a little louder, a little longer — so don\'t hammer it. If the word is recognisable, move on.',
  blockSize: (n) => (n > 4 ? 88 : n === 4 ? 100 : n === 3 ? 112 : 128),
}

export default function TurkishSoundsView({ lengths = [1, 2], count = 10, custom = '', voiceSpeed, onBack, onClose }) {
  const [items] = useState(() => buildItems(lengths, custom, count))
  return <SoundOutDrill plugin={plugin} items={items} voiceSpeed={voiceSpeed} onBack={onBack} onClose={onClose} />
}
